/* ---------- Imports ---------- */
import { api } from "core/axios/axios";
import type { ISupportTicket, ISupportPaginated } from "./types";

/* ---------- Adapter ---------- */
function adaptTicket(raw: any): ISupportTicket {
  return {
    id: String(raw?.id ?? ""),
    date: String(raw?.date ?? ""),
    subject: raw?.subject ?? "",
    status: raw?.status ?? "",
    description: raw?.description ?? "",
  };
}

/* ---------- API Calls ---------- */

/**
 * 🎫 Fetch all support tickets
 * 
 * API Response Format (indexed object):
 * {
 *   "1": { id: "REQ-11212415", date: "2024-11-21", subject: "billing", description: "testing", status: "In Progress" },
 *   "2": { id: "REQ-11272416", date: "2024-11-27", subject: "subscription", description: "test", status: "Open" },
 *   ...
 * }
 * 
 * Normalized Output (array):
 * [
 *   { id: "REQ-11212415", date: "2024-11-21", subject: "billing", description: "testing", status: "In Progress" },
 *   { id: "REQ-11272416", date: "2024-11-27", subject: "subscription", description: "test", status: "Open" },
 *   ...
 * ]
 */
/**
 * Fetches every support ticket.
 *
 * Shaped to what the endpoint actually does, verified live on 2026-09-09:
 * `GET /support` ignores `page`, `perPage` and every filter and returns the
 * whole list each time. So none of them are sent, and nothing is sliced here —
 * the screen receives what the server has, and CustomerView narrows it for
 * display, which is where its search and status filters already live.
 *
 * The response is an indexed object rather than an array, which is the one
 * shape difference worth normalising:
 *   { "1": {...}, "2": {...} }  ->  [ {...}, {...} ]
 */
export async function getSupportHistory(): Promise<ISupportPaginated<ISupportTicket>> {
  const { data } = await api.get<Record<string, any>>("support");
  const items: ISupportTicket[] = Object.values(data ?? {}).map(adaptTicket);

  return {
    data: items,
    total: items.length,
    page: 1,
    limit: items.length,
  };
}

/**
 * 🗑️ Delete support ticket by ID.
 */
export async function deleteSupportTicket(ticketId: string): Promise<void> {
  console.log("🗑️ [SUPPORT API] Deleting ticket:", ticketId);
  await api.delete(`support/${ticketId}`);
  console.log("✅ [SUPPORT API] Ticket deleted successfully");
}

/**
 * ➕ Create a new support ticket.
 */
export async function createSupportTicket(payload: any): Promise<ISupportTicket> {
  console.log("➕ [SUPPORT API] Creating new ticket:", payload);
  const { data } = await api.post("support", payload);
  const ticket = adaptTicket(data);
  console.log("✅ [SUPPORT API] Ticket created:", ticket);
  return ticket;
}

/**
 * ✏️ Update an existing support ticket.
 */
export async function updateSupportTicket(
  ticketId: string,
  payload: Partial<ISupportTicket>
): Promise<ISupportTicket> {
  console.log("✏️ [SUPPORT API] Updating ticket:", ticketId, payload);
  const { data } = await api.put(`support/${ticketId}`, payload);
  const ticket = adaptTicket(data);
  console.log("✅ [SUPPORT API] Ticket updated:", ticket);
  return ticket;
}
